import type { ParseKeys } from "i18next";
import type {
  Collection,
  CollectionBody,
  CollectionInput,
  CollectionRule,
  CollectionRuleColumn,
  CollectionRuleRelation,
  CollectionSortOrder,
  CollectionType,
  CollectionEbayChoice,
  MetafieldKey,
  MetafieldRef,
  ProductField,
  ProductMetafieldDefinition,
} from "@/lib/types";
import { trimAspectValues, type AspectValues } from "@/lib/ebay-aspects";

const TEXT_RELATIONS: CollectionRuleRelation[] = [
  "EQUALS",
  "NOT_EQUALS",
  "STARTS_WITH",
  "ENDS_WITH",
  "CONTAINS",
  "NOT_CONTAINS",
];

const NUMBER_RELATIONS: CollectionRuleRelation[] = [
  "EQUALS",
  "NOT_EQUALS",
  "GREATER_THAN",
  "LESS_THAN",
];

// what a rule's condition is entered with
export type RuleInput = "text" | "number" | "store" | "none";

// the rule columns the API matches, see ruleRelations in the product service
export const RULE_COLUMNS: Array<{
  value: CollectionRuleColumn;
  // translation key
  label: ParseKeys;
  relations: CollectionRuleRelation[];
  input: RuleInput;
}> = [
  {
    value: "TITLE",
    label: "collections.ruleColumns.TITLE",
    relations: TEXT_RELATIONS,
    input: "text",
  },
  {
    value: "VARIANT_TITLE",
    label: "collections.ruleColumns.VARIANT_TITLE",
    relations: TEXT_RELATIONS,
    input: "text",
  },
  {
    value: "VARIANT_SKU",
    label: "collections.ruleColumns.VARIANT_SKU",
    relations: TEXT_RELATIONS,
    input: "text",
  },
  {
    value: "VARIANT_PRICE",
    label: "collections.ruleColumns.VARIANT_PRICE",
    relations: NUMBER_RELATIONS,
    input: "number",
  },
  {
    value: "VARIANT_COMPARE_AT_PRICE",
    label: "collections.ruleColumns.VARIANT_COMPARE_AT_PRICE",
    relations: NUMBER_RELATIONS,
    input: "number",
  },
  {
    value: "VARIANT_INVENTORY",
    label: "collections.ruleColumns.VARIANT_INVENTORY",
    relations: NUMBER_RELATIONS,
    input: "number",
  },
  {
    value: "IS_PRICE_REDUCED",
    label: "collections.ruleColumns.IS_PRICE_REDUCED",
    relations: ["IS_SET", "IS_NOT_SET"],
    input: "none",
  },
  {
    value: "PLATFORM_CONNECTION",
    label: "collections.ruleColumns.PLATFORM_CONNECTION",
    relations: ["EQUALS", "NOT_EQUALS"],
    input: "store",
  },
];

export function ruleColumn(column: CollectionRuleColumn) {
  return RULE_COLUMNS.find((c) => c.value === column) ?? RULE_COLUMNS[0];
}

export const SORT_ORDERS: Array<{
  value: CollectionSortOrder;
  label: ParseKeys;
}> = [
  { value: "MANUAL", label: "collections.sort.MANUAL" },
  { value: "ALPHA_ASC", label: "collections.sort.ALPHA_ASC" },
  { value: "ALPHA_DESC", label: "collections.sort.ALPHA_DESC" },
  { value: "PRICE_DESC", label: "collections.sort.PRICE_DESC" },
  { value: "PRICE_ASC", label: "collections.sort.PRICE_ASC" },
  { value: "CREATED_DESC", label: "collections.sort.CREATED_DESC" },
  { value: "CREATED_ASC", label: "collections.sort.CREATED_ASC" },
];

// smart collections have no positions to sort by
export function sortOrdersFor(type: CollectionType) {
  return type === "SMART"
    ? SORT_ORDERS.filter((s) => s.value !== "MANUAL")
    : SORT_ORDERS;
}

export function newRule(): CollectionRule {
  return { column: "TITLE", relation: "CONTAINS", condition: "" };
}

// keeps the relation when the new column takes it
export function changeRuleColumn(
  rule: CollectionRule,
  column: CollectionRuleColumn,
): CollectionRule {
  const next = ruleColumn(column);
  const relation = next.relations.includes(rule.relation)
    ? rule.relation
    : next.relations[0];
  const condition =
    next.input === ruleColumn(rule.column).input ? rule.condition : "";

  return { column, relation, condition };
}

export function emptyCollectionInput(): CollectionInput {
  return {
    title: "",
    description: "",
    type: "MANUAL",
    rules: [newRule()],
    disjunctive: false,
    sort_order: "MANUAL",
    ebay_categories: {},
  };
}

export function collectionToInput(collection: Collection): CollectionInput {
  return {
    title: collection.title,
    description: collection.description,
    type: collection.type,
    rules: collection.rules.length > 0 ? collection.rules : [newRule()],
    disjunctive: collection.disjunctive,
    sort_order: collection.sort_order,
    ebay_categories: withEbayCategories(
      (collection.ebay_categories ?? []).map(
        ({ marketplace_id, category, aspects, metafields }) => [
          marketplace_id,
          { category, aspects: aspects ?? {}, metafields: metafields ?? {} },
        ],
      ),
    ),
  };
}

// item specifics in name order without the ones cleared, the API's and the
// aspects form's order can differ
function sortedAspects(aspects: AspectValues): AspectValues {
  return Object.fromEntries(
    Object.entries(aspects)
      .filter(([, values]) => values.length > 0)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
  );
}

// the metafields aspects are mapped to in aspect name order
function sortedMetafields(
  metafields: Record<string, MetafieldRef>,
): Record<string, MetafieldRef> {
  return Object.fromEntries(
    Object.entries(metafields)
      .map(([name, ref]): [string, MetafieldRef] => [
        name,
        isProductField(ref)
          ? { field: ref.field }
          : { namespace: ref.namespace, key: ref.key },
      ])
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
  );
}

// in marketplace order with their item specifics and metafields in name
// order, so the same ones compare equal
function withEbayCategories(
  entries: Array<[string, CollectionEbayChoice]>,
): Record<string, CollectionEbayChoice> {
  return Object.fromEntries(
    entries
      .map(([marketplaceId, choice]): [string, CollectionEbayChoice] => [
        marketplaceId,
        {
          category: choice.category,
          aspects: sortedAspects(choice.aspects),
          metafields: sortedMetafields(choice.metafields),
        },
      ])
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

/**
 * The collection's eBay categories with the marketplace's category, item
 * specifics and metafields set, or removed with null.
 */
export function withEbayCategory(
  categories: Record<string, CollectionEbayChoice>,
  marketplaceId: string,
  choice: CollectionEbayChoice | null,
): Record<string, CollectionEbayChoice> {
  const next = { ...categories, [marketplaceId]: choice };
  return withEbayCategories(
    Object.entries(next).filter(
      (entry): entry is [string, CollectionEbayChoice] => entry[1] !== null,
    ),
  );
}

// the fields of the Shopify product aspects can be mapped to, next to the
// metafields
export const PRODUCT_FIELDS: Array<{ field: ProductField; label: ParseKeys }> =
  [{ field: "vendor", label: "collections.form.productFieldVendor" }];

// the translation key of a product field's name
export function productFieldLabel(field: ProductField): ParseKeys {
  return (
    PRODUCT_FIELDS.find((option) => option.field === field)?.label ??
    "collections.form.productFieldVendor"
  );
}

export function isProductField(
  ref: MetafieldRef,
): ref is { field: ProductField } {
  return "field" in ref && !!ref.field;
}

// e.g. custom.brand, or field:vendor which no metafield can be
export function metafieldId(ref: MetafieldRef) {
  return isProductField(ref)
    ? `field:${ref.field}`
    : `${ref.namespace}.${ref.key}`;
}

/**
 * The name of a metafield's definition, or its namespace and key when none of
 * the stores has one, e.g. after it was deleted.
 */
export function metafieldName(
  ref: MetafieldKey,
  definitions: ProductMetafieldDefinition[] | undefined,
) {
  const id = metafieldId(ref);
  return (
    definitions?.find((definition) => metafieldId(definition) === id)?.name ??
    id
  );
}

/**
 * The metafields the category's aspects take their values from, without the
 * aspects it doesn't have, e.g. ones a newer version of the category dropped.
 */
export function aspectMetafields(
  aspectNames: string[],
  metafields: Record<string, MetafieldRef>,
): Record<string, MetafieldRef> {
  return Object.fromEntries(
    Object.entries(metafields).filter(([name]) => aspectNames.includes(name)),
  );
}

// the body the API takes, manual collections don't send rules and smart ones don't send products
export function collectionBody(input: CollectionInput): CollectionBody {
  const smart = input.type === "SMART";
  return {
    ...input,
    rules: smart ? input.rules : [],
    disjunctive: smart && input.disjunctive,
    product_ids: smart ? undefined : input.product_ids,
    ebay_categories: Object.fromEntries(
      Object.entries(input.ebay_categories).map(([marketplaceId, choice]) => [
        marketplaceId,
        choice.category.id,
      ]),
    ),
    ebay_aspects: Object.fromEntries(
      Object.entries(input.ebay_categories).map(([marketplaceId, choice]) => [
        marketplaceId,
        trimAspectValues(choice.aspects),
      ]),
    ),
    ebay_metafields: Object.fromEntries(
      Object.entries(input.ebay_categories).map(([marketplaceId, choice]) => [
        marketplaceId,
        choice.metafields,
      ]),
    ),
  };
}

// the API checks numeric conditions with the same pattern
const NUMBER_CONDITION = /^-?[0-9]+(\.[0-9]+)?$/;

/**
 * What's missing before the API takes the collection: a title, and a value for
 * every condition of a smart collection. Rules are listed by index.
 */
export function collectionErrors(input: CollectionInput) {
  const rules =
    input.type !== "SMART"
      ? []
      : input.rules.flatMap((rule, index) => {
          const { input: kind } = ruleColumn(rule.column);
          const condition = rule.condition.trim();
          const valid =
            kind === "none" ||
            (kind === "number"
              ? NUMBER_CONDITION.test(condition)
              : condition !== "");
          return valid ? [] : [index];
        });

  return { title: input.title.trim() === "", rules };
}

export function hasCollectionErrors(input: CollectionInput) {
  const errors = collectionErrors(input);
  return errors.title || errors.rules.length > 0;
}
